/*
* 操作日志管理组件
* 优化时间：2026-06-13 (重构为函数组件、Hooks、性能优化)
*/

import CNF from '../../incs/Config.js';
import {
    UjoinParams,
    AddRequestHeaders,
    isGTscreenWidth,
} from '../../incs/FunHelper.js';

import { useState, useEffect, useCallback, useMemo } from 'react';
import $ from 'jquery';
import {
    theme,
    message,
    Table,
    Input,
    Select,
    Space,
    Spin,
    Empty,
} from 'antd';

const { Option } = Select;

// 子组件：表格列表
const Lists = ({ dataLists, currentIndex, pageCount, changeIndex, loading }) => {
    // 使用 useMemo 缓存 columns，避免每次渲染重新创建
    const columns = useMemo(() => [
        { title: '', dataIndex: '', key: 'key', fixed: 'left', width: CNF.searchMarginLeft * 4 },
        { title: '操作者', dataIndex: 'operation', key: 'operation', fixed: 'left' },
        { title: '操作对象', dataIndex: 'instance', key: 'instance' },
        { title: '模块', dataIndex: 'module', key: 'module' },
        { title: '动作', dataIndex: 'action', key: 'action' },
        {
            title: '描述', key: 'description', ellipsis: true,
            render: (record) => record.description || '-'
        },
        {
            title: '创建时间',
            dataIndex: 'create_time',
            key: 'create_time',
            sorter: (a, b) => Date.parse(a.create_time) - Date.parse(b.create_time),
            width: CNF.tableCreateTimeColumnWidth + 60
        },
    ], []);

    return (
        <Table
            columns={columns}
            size='middle'
            dataSource={dataLists}
            showSorterTooltip={{ target: 'sorter-icon' }}
            pagination={{
                showSizeChanger: false,
                showQuickJumper: false,
                showTotal: () => `共${pageCount > 10000 ? (pageCount / 10000).toFixed(CNF.DPL) + '万' : pageCount}条`,
                pageSize: CNF.pageSize,
                current: currentIndex,
                total: pageCount,
                onChange: (current) => changeIndex(current),
            }}
        />
    );
};

// 主组件
const LogOperation = ({ btnLists }) => {
    const { token: { messageDuration } } = theme.useToken();

    // 状态定义
    const [loading, setLoading] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(1);
    const [pageCount, setPageCount] = useState(0);
    const [dataLists, setDataLists] = useState([]);

    // 搜索条件状态
    const [operation, setOperation] = useState('');
    const [instance, setInstance] = useState('');
    const [module, setModule] = useState('');
    const [action, setAction] = useState('');

    // 获取列表数据
    const fetchLists = useCallback(() => {
        setLoading(true);

        // 1. 构建原始参数对象
        const rawParams = {
            operation: operation,
            instance: instance,
            module: module,
            action: action,
            page_size: isGTscreenWidth ? CNF.pageSize : CNF.pageSize - 2,
            current_index: currentIndex
        };

        // 2. 过滤掉空值 (空字符串, null, undefined)
        // 注意：如果 0 也是有效值，需要额外处理，但此处页码和数量通常不为0或空
        const params = Object.fromEntries(
            Object.entries(rawParams).filter(([_, value]) => value !== '' && value !== null && value !== undefined)
        );

        $.ajax({
            url: `${CNF.api}logs/operations${UjoinParams(params)}`,
            headers: AddRequestHeaders(),
            type: 'GET',
            success: (data) => {
                if (data.res === 0) {
                    setDataLists(data.dat || []);
                    setPageCount(data.cnt || 0);
                } else {
                    message.error(data.msg, messageDuration);
                }
            },
            error: () => {
                message.error('网络请求失败', messageDuration);
            },
            complete: () => {
                setLoading(false);
            }
        });
    }, [operation, instance, module, action, currentIndex, messageDuration]);

    // 监听搜索条件和页码变化，自动获取数据
    useEffect(() => {
        fetchLists();
    }, [fetchLists]);

    // 事件处理函数
    const handleOperationChange = useCallback((e) => {
        setOperation(e.target.value);
        setCurrentIndex(1); // 搜索时重置页码
    }, []);

    const handleInstanceChange = useCallback((e) => {
        setInstance(e.target.value);
        setCurrentIndex(1); // 搜索时重置页码
    }, []);

    const handleModuleChange = useCallback((value) => {
        setModule(value || '');
        setCurrentIndex(1); // 搜索时重置页码
    }, []);

    const handleActionChange = useCallback((value) => {
        setAction(value || '');
        setCurrentIndex(1); // 搜索时重置页码
    }, []);

    const handleIndexChange = useCallback((index) => {
        setCurrentIndex(index);
    }, []);

    // 生成模块选项
    const moduleOptions = useMemo(() => {
        const options = [];
        Object.keys(CNF.navItems).forEach((key) => {
            const item = CNF.navItems[key];
            if (item.submenu) {
                item.submenu.forEach(sub => {
                    if (sub.isLogModule && btnLists?.indexOf(sub.ptn) >= 0) {
                        options.push(<Option key={sub.ptn} value={sub.name}>{sub.name}</Option>);
                    }
                });
            } else if (item.isLogModule && btnLists?.indexOf(item.ptn) >= 0) {
                options.push(<Option key={item.ptn} value={item.name}>{item.name}</Option>);
            }
        });
        return options;
    }, [btnLists]);

    // 生成动作选项
    const actionOptions = useMemo(() => {
        return Object.keys(CNF.actionName).map((key) => {
            const actionName = CNF.actionName[key];
            if (btnLists?.indexOf(key.toLowerCase()) > -1) {
                return <Option key={key} value={actionName}>{actionName}</Option>;
            }
            return null;
        }).filter(Boolean);
    }, [btnLists]);

    return (
        <>
            <div style={{ paddingTop: CNF.searchMarginLeft * 4, paddingBottom: CNF.searchMarginLeft * 4, paddingLeft: CNF.searchMarginLeft * 4 }}>
                <Space>
                    <Input
                        onChange={handleOperationChange}
                        placeholder='操作者'
                        value={operation}
                        allowClear
                    />
                    <Input
                        onChange={handleInstanceChange}
                        placeholder='操作对象'
                        value={instance}
                        allowClear
                    />
                    <Select
                        allowClear
                        placeholder='模块'
                        style={{ width: '100%', minWidth: 120, }}
                        popupMatchSelectWidth={false}
                        onChange={handleModuleChange}
                        value={module || undefined}
                    >
                        {moduleOptions}
                    </Select>
                    <Select
                        allowClear
                        placeholder='动作'
                        style={{ width: '100%', minWidth: 80, }}
                        popupMatchSelectWidth={false}
                        onChange={handleActionChange}
                        value={action || undefined}
                    >
                        {actionOptions}
                    </Select>
                </Space>
            </div>
            <Lists
                dataLists={dataLists}
                currentIndex={currentIndex}
                pageCount={pageCount}
                changeIndex={handleIndexChange}
                loading={loading}
            />
            {   /* 空数据 */
                dataLists.length === 0 && <Empty />
            }
            {
                loading && <Spin description='加载中...'><div style={{ height: 100 }}></div></Spin>
            }
        </>
    );
};

export default LogOperation;